'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Phone,
  Laptop,
  MessageSquare,
  ArrowRight,
  ExternalLink,
  Copy,
  Check,
  MailIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CallbackRequestForm } from './callback-request-form';
interface ContactValeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactValeDialog({
  open,
  onOpenChange
}: ContactValeDialogProps) {
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText('support@vale.legal');
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const handleBookMeeting = () => {
    window.open('https://cal.com/team/vale/vale-support', '_blank');
  };

  const handleCallbackSuccess = () => {
    setShowCallbackForm(false);
    onOpenChange(false);
  };

  // Reset form state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setShowCallbackForm(false);
    }
    onOpenChange(newOpen);
  };

  if (showCallbackForm) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <CallbackRequestForm
            onSuccess={handleCallbackSuccess}
            onBack={() => setShowCallbackForm(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        style={{
          display: 'flex',
          maxWidth: '500px',
          padding: '18px 24px 24px 24px',
          flexDirection: 'column'
        }}
      >
        <DialogHeader>
          <DialogTitle
            // VALE-531: `color: '#000'` moved onto the token; the typography
            // overrides stay inline where they were.
            className="text-foreground"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '12px',
              letterSpacing: '-0.28px'
            }}
          >
            Speak with the Vale team
          </DialogTitle>
          <DialogDescription
            style={{
              color: 'var(--grey-45)',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '12px',
              letterSpacing: '-0.24px'
            }}
          >
            We're here to help
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* Request a callback */}
          <div className="hover:bg-accent/50 flex items-center justify-between rounded-lg border p-4 transition-colors">
            <div className="flex items-start gap-4">
              <div className="bg-info-subtle flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-md p-1">
                <Phone className="text-info-subtle-foreground h-4 w-4" />
              </div>
              <div className="space-y-1">
                <h3
                  style={{
                    color: 'var(--grey-900)',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '12px',
                    letterSpacing: '-0.24px'
                  }}
                >
                  Request a callback.
                </h3>
                <p
                  style={{
                    color: 'var(--grey-45)',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '14px',
                    letterSpacing: '-0.24px'
                  }}
                >
                  We'll give you a call as soon as we can.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowCallbackForm(true)}
            >
              Request
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Book a meeting */}
          <div className="hover:bg-accent/50 flex items-center justify-between rounded-lg border p-4 transition-colors">
            <div className="flex items-start gap-4">
              <div className="bg-info-subtle flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-md p-1">
                <Laptop className="text-info-subtle-foreground h-4 w-4" />
              </div>
              <div className="space-y-1">
                <h3
                  style={{
                    color: 'var(--grey-900)',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '12px',
                    letterSpacing: '-0.24px'
                  }}
                >
                  Book a meeting
                </h3>
                <p
                  style={{
                    color: 'var(--grey-45)',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '14px',
                    letterSpacing: '-0.24px'
                  }}
                >
                  Pick a time that works for you.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleBookMeeting}
            >
              Book
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>

          {/* Send us an email */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-start gap-4">
              <div className="bg-info-subtle flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-md p-1">
                <MessageSquare className="text-info-subtle-foreground h-4 w-4" />
              </div>
              <div className="space-y-1">
                <h3
                  style={{
                    color: 'var(--grey-900)',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: '12px',
                    letterSpacing: '-0.24px'
                  }}
                >
                  Send us an email
                </h3>
                <div className="flex items-center gap-2">
                  <p
                    style={{
                      color: 'var(--grey-45)',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '14px',
                      letterSpacing: '-0.24px'
                    }}
                  >
                    Email us at support@vale.legal
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto w-auto p-0"
                    onClick={handleCopyEmail}
                  >
                    {emailCopied ? (
                      <Check className="text-success-subtle-foreground h-3.5 w-3.5" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    <span className="sr-only">Copy email</span>
                  </Button>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="self-center"
              onClick={() =>
                (window.location.href = 'mailto:support@vale.legal')
              }
            >
              <MailIcon className="text-info-subtle-foreground h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
